import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useListFormatThemeQuery } from 'hooks/queries';
import DashboardCard from 'ui/components/DashboardCard/DashboardCard';
import { Box, Typography, useTheme } from '@mui/material';
import styles from './SharingLinksCard.styles';
import SharingLinkBlock from './components/SharingLinkBlock';
import { getUrlApplicationLink, getUrlSelfRegistrationLink, getUrlEvergreenLink } from 'utils/company';
import clipboardCopy from 'clipboard-copy';
import { useAlert } from 'common/alertContext';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TextField from 'ui/components/form/TextField';
import Button from 'ui/components/Button/Button';
import { useSendEmailToSpecificAddress } from '../../../../../hooks/usecase/useSendEmailToSpecificAddress';

const SharingLinksCard = ({ company = {} }) => {
  const { setStateAlert } = useAlert();
  const { t } = useTranslation();
  const theme = useTheme();
  const themedStyles = styles(theme);

  const { selfRegistrationActivated, applicationLink, selfRegistrationLink, evergreenLink } = company;

  const { data: listThemeData } = useListFormatThemeQuery(
    company.id,
    { active: true },
    { enabled: company.selfRegistrationActivated }
  );

  const { state, actions } = useSendEmailToSpecificAddress();

  const onClickApplicationForm = () => {
    clipboardLink(getUrlApplicationLink(applicationLink));
  };

  const onClickOpenMentoring = () => {
    clipboardLink(getUrlSelfRegistrationLink(selfRegistrationLink, applicationLink, listThemeData[0].id));
  };

  const onClickEvergreen = () => {
    clipboardLink(getUrlEvergreenLink(evergreenLink));
  };

  const onClickSendEmail = (type) => {
    actions.clickSendEmail(type);
  };

  const clipboardLink = (link) => {
    clipboardCopy(link).catch((err) => {
      console.error('Copy failed:', err);
    });
    setStateAlert({ open: true, message: t('sharing_links_card.msg_clipboard') });
  };

  const getDialogTitle = () => {
    switch (state.nav.linkType) {
      case 'application_form':
        return t('sharing_links_card.title_send_applicationlink_to_collaborator');
      case 'open_mentoring':
        return t('sharing_links_card.title_send_openmentoring_to_collaborator');
      case 'evergreen':
        return t('sharing_links_card.title_send_evergreen_to_collaborator');
      default:
        return t('sharing_links_card.title_send_applicationlink_to_collaborator');
    }
  };

  const getDialogDescription = () => {
    switch (state.nav.linkType) {
      case 'application_form':
        return t('sharing_links_card.description_send_applicationlink_to_collaborator');
      case 'open_mentoring':
        return t('sharing_links_card.description_send_openmentoring_to_collaborator');
      case 'evergreen':
        return t('sharing_links_card.description_send_evergreen_to_collaborator');
      default:
        return t('sharing_links_card.description_send_applicationlink_to_collaborator');
    }
  };

  return (
    <>
      <DashboardCard sx={themedStyles.card}>
        <Box>
          <Typography sx={themedStyles.title}>{t('sharing_links_card.title')}</Typography>
        </Box>
        <SharingLinkBlock
          clientLabel={company.clientLabel}
          expertLabel={company.expertLabel}
          type="application_form"
          onClickCopy={onClickApplicationForm}
          onClickSendEmail={onClickSendEmail}
        />

        <Box sx={themedStyles.divider} />

        {selfRegistrationActivated && (
          <SharingLinkBlock
            clientLabel={company.clientLabel}
            expertLabel={company.expertLabel}
            type="open_mentoring"
            onClickCopy={onClickOpenMentoring}
            onClickSendEmail={onClickSendEmail}
          />
        )}
        {!selfRegistrationActivated && (
          <SharingLinkBlock
            clientLabel={company.clientLabel}
            expertLabel={company.expertLabel}
            type="evergreen"
            onClickCopy={onClickEvergreen}
            onClickSendEmail={onClickSendEmail}
          />
        )}
      </DashboardCard>

      <Dialog
        open={state.nav.showDialog}
        onClose={actions.close}
        fullWidth
        maxWidth="sm" // sm, md ou lg selon la taille que tu veux
        PaperProps={{
          sx: { borderRadius: 3, p: 2 }, // arrondis et padding
        }}>
        {/* Bouton de fermeture en haut à droite */}
        <IconButton
          aria-label="close"
          onClick={actions.close}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}>
          <CloseIcon />
        </IconButton>

        {/* Titre dynamique selon le type */}
        <DialogTitle
          sx={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            pb: 0,
          }}>
          {getDialogTitle()}
        </DialogTitle>

        {/* Texte explicatif */}
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {getDialogDescription()}
          </Typography>

          {/* Champ */}
          <TextField fullWidth label={t('email')} name="email" control={state.form.control} />
        </DialogContent>

        {/* Actions */}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="secondary" onClick={actions.close}>
            {t('cancel')}
          </Button>
          <Button onClick={actions.submit} disabled={actions.isLoading}>
            {actions.isLoading ? t('Sending...') : t('sharing_links_card.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SharingLinksCard;
